import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, User, Car, MapPin, Users, Loader2, Flame } from "lucide-react";

const SRC_COLOR = { CAD: "#3B82F6", RMS: "#10B981", FI: "#8B5CF6" };
const box = "bg-[#141518] border border-[#2C2D35] p-3";
const lblCls = "font-mono text-[10px] text-[#9CA3AF] uppercase mb-2 flex items-center gap-1.5";

function HitRow({ h }) {
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-[#1E1F24] text-[11px] font-mono">
      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold shrink-0" style={{ background: `${SRC_COLOR[h.source]}22`, color: SRC_COLOR[h.source], border: `1px solid ${SRC_COLOR[h.source]}55` }}>{h.source}</span>
      <span className="text-[#22D3EE] shrink-0">{h.ref}</span>
      <span className="text-white">{h.type}</span>
      {h.role && <span className="text-[#F59E0B]">{h.role}</span>}
      <span className="text-[#9CA3AF] truncate">{h.address}</span>
      <span className="ml-auto text-[#6B7280] shrink-0">{h.ts ? new Date(h.ts).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }) : "—"}</span>
    </div>
  );
}

export default function Links() {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [hotList, setHotList] = useState(null);
  const nav = useNavigate();
  const [params] = useSearchParams();

  // ?q= deep link (one-tap "Links" from any incident's person/plate).
  useEffect(() => {
    const qp = params.get("q");
    if (qp) { setQ(qp); lookup(qp); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  useEffect(() => {
    client.get("/links/hot").then((r) => setHotList(r.data)).catch(() => {});
  }, []);

  const lookup = async (query) => {
    const term = (query ?? q).trim();
    if (term.length < 2) { toast.error("Enter a name or plate (2+ characters)"); return; }
    if (query != null) setQ(query);
    setBusy(true);
    try {
      const { data } = await client.get("/links/lookup", { params: { q: term } });
      setRes(data);
      if (!data.total) toast.info(`No linked records found for “${term}”`);
    } catch (e) { toast.error(e.response?.data?.detail || "Lookup failed"); }
    finally { setBusy(false); }
  };

  const p = res?.profile || {};
  return (
    <Layout>
      <PageHeader title="Person / Vehicle Links" sub="Every incident, address and associate connected to a name or plate — CAD entities · RMS reports · Field Interviews (lookups audited)" />
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="flex gap-2 max-w-2xl">
          <div className="flex-1 flex items-center gap-2 bg-[#141518] border border-[#2C2D35] px-3 py-2.5 focus-within:border-[#3B82F6]">
            <Search size={15} className="text-[#6B7280]" />
            <input data-testid="links-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && lookup()}
              placeholder="Person name (e.g. Jane Witness) or plate (e.g. ABC-1234)…"
              className="flex-1 bg-transparent outline-none text-sm font-mono text-white placeholder:text-[#6B7280]" />
          </div>
          <button data-testid="links-search-btn" onClick={() => lookup()} disabled={busy}
            className="px-5 py-2.5 text-sm font-head font-bold bg-[#3B82F6] hover:bg-[#2563eb] text-white transition-colors disabled:opacity-50 flex items-center gap-2">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />} LOOKUP
          </button>
        </div>

        {res && (
          <>
            {/* Identity profile */}
            <div className={box} data-testid="links-profile">
              <div className={lblCls}><User size={12} /> Identity Profile — “{res.query}” · {res.total} linked record{res.total === 1 ? "" : "s"}
                {res.hot && (
                  <span data-testid="links-hot-badge" className="px-2 py-0.5 rounded text-[10px] font-bold text-white bg-[#EA580C] animate-pulse"
                    title={`${res.recent_30d} linked records in the last 30 days (prior 30: ${res.prior_30d})`}>
                    🔥 HOT — {res.recent_30d} IN 30 DAYS{res.recent_30d > res.prior_30d ? " ▲ RISING" : ""}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-[12px] font-mono">
                {p.names?.length > 0 && <span className="text-white">Names: <b>{p.names.join(" · ")}</b></span>}
                {p.dobs?.length > 0 && <span className="text-[#F59E0B]">DOB: {p.dobs.join(", ")}</span>}
                {p.sexes?.length > 0 && <span className="text-[#9CA3AF]">Sex: {p.sexes.join(", ")}</span>}
                {p.plates?.length > 0 && <span className="text-[#22D3EE]">Plates: {p.plates.join(", ")}</span>}
                {p.vehicle_desc?.length > 0 && <span className="text-[#9CA3AF]">Vehicle: {p.vehicle_desc.join(" | ")}</span>}
                {res.total === 0 && <span className="text-[#6B7280]">No CAD, RMS or FI records reference this name/plate.</span>}
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {/* Incident history */}
              <div className={box} data-testid="links-incidents">
                <div className={lblCls}><Search size={12} /> Incident History ({res.person_hits.length + res.vehicle_hits.length})</div>
                {[...res.person_hits, ...res.vehicle_hits].sort((a, b) => (b.ts || "").localeCompare(a.ts || "")).map((h, i) => <HitRow key={i} h={h} />)}
                {res.total === 0 && <div className="text-[11px] font-mono text-[#6B7280]">None.</div>}
              </div>

              <div className="space-y-4">
                {/* Connected addresses */}
                <div className={box} data-testid="links-addresses">
                  <div className={lblCls}><MapPin size={12} /> Connected Addresses ({res.addresses.length})</div>
                  {res.addresses.map((a, i) => (
                    <div key={i} className="flex items-center gap-2 py-1 text-[11px] font-mono border-b border-[#1E1F24]">
                      <span className="text-white">{a.address}</span>
                      <span className="text-[#F59E0B]">×{a.count}</span>
                      <button data-testid={`links-addr-intel-${i}`} onClick={() => nav(`/premise?address=${encodeURIComponent(a.address)}`)}
                        className="ml-auto text-[10px] text-[#3B82F6] hover:underline shrink-0">premise →</button>
                    </div>
                  ))}
                  {res.addresses.length === 0 && <div className="text-[11px] font-mono text-[#6B7280]">None.</div>}
                </div>

                {/* Known associates */}
                <div className={box} data-testid="links-associates">
                  <div className={lblCls}><Users size={12} /> Known Associates ({res.associates.length})</div>
                  {res.associates.map((a, i) => (
                    <div key={i} className="flex items-center gap-2 py-1 text-[11px] font-mono border-b border-[#1E1F24]">
                      <button data-testid={`links-assoc-${i}`} onClick={() => lookup(a.name)}
                        className="text-[#22D3EE] hover:underline font-bold">{a.name}</button>
                      {a.roles?.length > 0 && <span className="text-[#F59E0B]">{a.roles.join("/")}</span>}
                      <span className="text-[#9CA3AF]">on {a.refs.join(", ")}</span>
                    </div>
                  ))}
                  {res.associates.length === 0 && <div className="text-[11px] font-mono text-[#6B7280]">None.</div>}
                </div>

                {/* Linked vehicles */}
                <div className={box} data-testid="links-vehicles">
                  <div className={lblCls}><Car size={12} /> Linked Vehicles ({res.vehicles_linked.length})</div>
                  {res.vehicles_linked.map((v, i) => (
                    <div key={i} className="flex items-center gap-2 py-1 text-[11px] font-mono border-b border-[#1E1F24]">
                      <button data-testid={`links-veh-${i}`} onClick={() => lookup(v.plate)}
                        className="text-[#22D3EE] hover:underline font-bold">{v.plate}</button>
                      <span className="text-[#9CA3AF]">{v.desc}</span>
                      <span className="ml-auto text-[#6B7280]">on {v.refs.join(", ")}</span>
                    </div>
                  ))}
                  {res.vehicles_linked.length === 0 && <div className="text-[11px] font-mono text-[#6B7280]">None.</div>}
                </div>
              </div>
            </div>
          </>
        )}
        {!res && (
          <>
            <div className="text-[12px] font-mono text-[#6B7280] max-w-2xl">
              Search a person's name or a license plate. The link engine correlates CAD call entities,
              RMS report persons/vehicles and Field Interview cards into one profile: incident history,
              connected addresses, known associates and linked vehicles. Tap any associate or plate to pivot.
              Every lookup is audited.
            </div>
            {/* Hot repeat players — instant view of who keeps coming up */}
            <div className={`${box} max-w-4xl`} data-testid="links-hot-panel">
              <div className={lblCls}><Flame size={12} className="text-[#EA580C]" /> Hot Repeat Players — 3+ linked incidents in the last {hotList?.days ?? 30} days</div>
              {!hotList && <div className="text-[11px] font-mono text-[#6B7280]">Scanning…</div>}
              {hotList && hotList.hot_persons.length === 0 && hotList.hot_vehicles.length === 0 && (
                <div className="text-[11px] font-mono text-[#10B981]">✓ No repeat players above threshold right now.</div>
              )}
              {hotList?.hot_persons.map((p, i) => (
                <div key={`hp${i}`} className="flex items-center gap-2 py-1.5 text-[11px] font-mono border-b border-[#1E1F24]">
                  <User size={12} className="text-[#60A5FA] shrink-0" />
                  <button data-testid={`links-hot-person-${i}`} onClick={() => lookup(p.label)}
                    className="text-[#22D3EE] hover:underline font-bold">{p.label}</button>
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold text-white bg-[#EA580C]">🔥 {p.recent} in {hotList.days}d{p.spike ? " ▲" : ""}</span>
                  <span className="text-[#9CA3AF]">total {p.total} · {p.refs.slice(0, 4).join(", ")}</span>
                </div>
              ))}
              {hotList?.hot_vehicles.map((v, i) => (
                <div key={`hv${i}`} className="flex items-center gap-2 py-1.5 text-[11px] font-mono border-b border-[#1E1F24]">
                  <Car size={12} className="text-[#4ade80] shrink-0" />
                  <button data-testid={`links-hot-vehicle-${i}`} onClick={() => lookup(v.label)}
                    className="text-[#22D3EE] hover:underline font-bold">{v.label}</button>
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold text-white bg-[#EA580C]">🔥 {v.recent} in {hotList.days}d{v.spike ? " ▲" : ""}</span>
                  <span className="text-[#9CA3AF]">{v.desc} · {v.refs.slice(0, 4).join(", ")}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
